import type {
  ImmutableSheetGeometryList,
  SheetGeometry,
  SheetGeometryList,
} from "building-types/views/sheetGeometryTypes";
import { SheetGeometryType } from "building-types/views/sheetGeometryTypes";
import { assertUnreachable } from "common/utils/assertUnreachable";
import { aabb2ByPolyLine2s, aabb2Move, aabb2UnionAll } from "geom/aabb2";
import type { AABB2 } from "geom/aabb2Types";
import type { OBB2 } from "geom/obb2";
import { obb2FromAABB2, obb2New, obb2Rotate } from "geom/obb2";
import { polyLine2AABB2, polyLine2Rotate } from "geom/polyLine2";
import { vec2RotateAround } from "geom/vec2";
import { SVGBoundingBoxProvider } from "svg/svgBoundingBoxProvider";
import { arcToPolyLine } from "./primitives/arcPrimitive";
import { sheetAssetDimensions } from "./primitives/assetPrimitive";
import {
  planTextBoxPerimeter,
  planTextGeometryOBB,
  transformTextGeometryForInvertedY,
} from "./primitives/textPrimitive";
import { sheetSVGGeometryScale } from "./sheetGeometryTools";

const defaultSheetGeometryTypeAABB2Filter: { [K in SheetGeometryType]?: boolean } = {
  [SheetGeometryType.SVG]: true,
  [SheetGeometryType.CIRCLE]: true,
  [SheetGeometryType.ARC]: true,
  [SheetGeometryType.POLYGON]: true,
  [SheetGeometryType.POLYLINE]: true,
  [SheetGeometryType.ASSET]: true,
};

export const allSheetGeometryTypeAABB2Filter: { [K in SheetGeometryType]: true } = {
  [SheetGeometryType.SVG]: true,
  [SheetGeometryType.CIRCLE]: true,
  [SheetGeometryType.ARC]: true,
  [SheetGeometryType.POLYGON]: true,
  [SheetGeometryType.POLYLINE]: true,
  [SheetGeometryType.TEXT]: true,
  [SheetGeometryType.ASSET]: true,
};

export default function sheetGeometryListAABB2(
  rendering: SheetGeometryList | ImmutableSheetGeometryList,
  rotationRad: number = 0,
  filter = defaultSheetGeometryTypeAABB2Filter,
): AABB2 {
  return aabb2UnionAll(
    rendering.geometry
      .filter((geom) => filter[geom.type])
      .map((entity) => sheetGeometryAABB2(entity, rotationRad))
      .filter((x) => !!x),
  );
}

// Expects the text geometry to be normalized and styled
export function sheetGeometryAABB2(geom: SheetGeometry, rotationRad: number = 0): AABB2 | null {
  const centerX: number = 0;
  const centerY: number = 0;
  switch (geom.type) {
    case SheetGeometryType.SVG: {
      const [rotatedX, rotatedY] = vec2RotateAround(geom.x, geom.y, rotationRad, centerX, centerY);
      const scale = sheetSVGGeometryScale(geom);
      // We want to rotate the svg in sheet space around the sheet origin.
      // getAABB2FromSVGString rotates the svg in SVG space which is y-down, hence the rotation is inverted and the
      // translation is 0,0.
      const aabb = SVGBoundingBoxProvider.instance.getAABB2FromSVGString(geom.path, 0, 0, -rotationRad, scale, scale);

      if (!aabb) {
        return null;
      }

      // aabb is in SVG space, so we need to mirror it and move it to the rotated position.
      // UNFORTUNATELY that doesn't match the rendering of the SVGs as it is.
      // TODO: return aabb2Move(aabb2MirrorY(aabb), rotatedX, rotatedY);

      // Therefore we are returning the aabb in this weird space, where the position is in sheet space,
      // but the aabb is in SVG space.
      return aabb2Move(aabb, rotatedX, rotatedY);
    }
    case SheetGeometryType.CIRCLE: {
      const circle = geom;
      const r = circle.radius;
      const [rotatedX, rotatedY] = vec2RotateAround(circle.x, circle.y, rotationRad, centerX, centerY);

      return {
        minX: rotatedX - r,
        maxX: rotatedX + r,
        minY: rotatedY - r,
        maxY: rotatedY + r,
      };
    }
    case SheetGeometryType.ARC: {
      // TODO: more efficient and accurate implementation
      return aabb2ByPolyLine2s(arcToPolyLine(geom).paths, rotationRad, centerX, centerY);
    }
    case SheetGeometryType.POLYGON: {
      return aabb2UnionAll(
        geom.paths.map((x) => aabb2ByPolyLine2s(x, rotationRad, centerX, centerY)).filter((x) => !!x),
      );
    }
    case SheetGeometryType.POLYLINE: {
      return aabb2ByPolyLine2s(geom.paths, rotationRad, centerX, centerY);
    }
    case SheetGeometryType.TEXT: {
      const perimeter = planTextBoxPerimeter(transformTextGeometryForInvertedY(geom));
      if (rotationRad) {
        polyLine2Rotate(perimeter, rotationRad, centerX, centerY);
      }
      return polyLine2AABB2(perimeter);
    }
    case SheetGeometryType.ASSET: {
      const { outline } = sheetAssetDimensions(geom);
      polyLine2Rotate(outline, rotationRad, centerX, centerY);
      return polyLine2AABB2(outline);
    }
    default: {
      assertUnreachable(geom);
    }
  }
}

export function sheetGeometryOBB2(geom: SheetGeometry, rotationRad: number = 0): OBB2 | null {
  const centerX: number = 0;
  const centerY: number = 0;
  switch (geom.type) {
    case SheetGeometryType.SVG: {
      const scale = sheetSVGGeometryScale(geom);

      // aabb is in SVG space. To save the aabb2MirrorY, we cheat and scale the y-axis by -1. This works only
      // because the SVG is not rotated.
      const aabb = SVGBoundingBoxProvider.instance.getAABB2FromSVGString(geom.path, geom.x, geom.y, 0, scale, -scale);

      if (!aabb) {
        return null;
      }

      const obb = obb2FromAABB2(aabb);

      return obb2Rotate(obb, rotationRad, centerX, centerY);
    }
    case SheetGeometryType.CIRCLE: {
      const obb = obb2New(geom.x, geom.y, geom.radius, geom.radius, 0);

      return obb2Rotate(obb, rotationRad, centerX, centerY);
    }
    case SheetGeometryType.ARC: {
      // TODO: more efficient and accurate implementation
      const aabb = aabb2ByPolyLine2s(arcToPolyLine(geom).paths);

      if (!aabb) {
        return null;
      }

      const obb = obb2FromAABB2(aabb);

      return obb2Rotate(obb, rotationRad, centerX, centerY);
    }
    case SheetGeometryType.POLYGON: {
      const aabb = aabb2UnionAll(geom.paths.map((x) => aabb2ByPolyLine2s(x)).filter((x) => !!x));

      if (!aabb) {
        return null;
      }

      const obb = obb2FromAABB2(aabb);

      return obb2Rotate(obb, rotationRad, centerX, centerY);
    }
    case SheetGeometryType.POLYLINE: {
      const aabb = aabb2ByPolyLine2s(geom.paths);

      if (!aabb) {
        return null;
      }

      const obb = obb2FromAABB2(aabb);

      return obb2Rotate(obb, rotationRad, centerX, centerY);
    }
    case SheetGeometryType.TEXT: {
      const obb = planTextGeometryOBB(geom);

      return obb2Rotate(obb, rotationRad, centerX, centerY);
    }
    case SheetGeometryType.ASSET: {
      const { width, height, centerX, centerY, rotationRads } = sheetAssetDimensions(geom);
      return obb2New(centerX, centerY, width * 0.5, height * 0.5, rotationRads);
    }
    default: {
      assertUnreachable(geom);
    }
  }
}
